// =============================================================================
// SiteMapsCard — the project's / BID's site maps, on their own pages
// (mig 390, 26 Sep 2026)
// =============================================================================
// One card, mounted twice: the project HSE tab (the mud map lives in the
// project's site folder — James, 26 Sep) and the BID page (MOPs get drawn
// before the job is won). A project also shows the maps drawn on the BID it
// came from, through projects.source_bid_id — nothing moves on promote.
// =============================================================================

import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Map as MapIcon, Plus, Lock } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Card, Pill, Spinner, Button } from "@/components/ui";
import { thumbUrl } from "./siteMapThumb";

function fmtDate(d?: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
}

export default function SiteMapsCard({
  projectId, sourceBidId, bidId, canEdit,
}: {
  projectId?: string | null;
  sourceBidId?: string | null;   // project page: the BID this project came from
  bidId?: string | null;
  canEdit: boolean;
}) {
  const ownerKey = projectId ? `p:${projectId}:${sourceBidId ?? ""}` : `b:${bidId ?? ""}`;
  const { data: maps = [], isLoading } = useQuery({
    queryKey: ["site_maps_card", ownerKey],
    enabled: !!(projectId || bidId),
    queryFn: async () => {
      let q = (supabase as any).from("site_maps")
        .select("site_map_id, map_number, title, centre_lat, centre_lng, version, locked_at, is_locked, updated_at, features, project_id, bid_id")
        .eq("is_active", true)
        .order("updated_at", { ascending: false });
      if (projectId) {
        q = sourceBidId
          ? q.or(`project_id.eq.${projectId},bid_id.eq.${sourceBidId}`)
          : q.eq("project_id", projectId);
      } else {
        q = q.eq("bid_id", bidId);
      }
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  const newHref = projectId ? `/hse/site-maps?project=${projectId}` : `/hse/site-maps?bid=${bidId}`;

  return (
    <Card>
      <div className="p-4">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="text-[11px] uppercase tracking-wider text-text-muted flex items-center gap-1.5">
            <MapIcon size={12}/> Site maps {maps.length > 0 && <span className="font-mono">({maps.length})</span>}
          </div>
          {canEdit && (
            <Link to={newHref}><Button variant="ghost" icon={Plus} size="sm">New map</Button></Link>
          )}
        </div>
        {isLoading ? (
          <div className="py-3 flex justify-center"><Spinner/></div>
        ) : maps.length === 0 ? (
          <div className="py-2 text-[12px] text-text-muted">
            No map drawn yet. {canEdit ? "New map centres on the site's GPS — draw the mud map over the satellite image; a manager locks the version the paperwork prints." : "The crew draws it over the satellite image."}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {maps.map((m: any) => {
              const thumb = thumbUrl(m.centre_lat, m.centre_lng, 16);
              const n = m.features?.features?.length ?? 0;
              const fromBid = !!(projectId && !m.project_id && m.bid_id);
              return (
                <Link key={m.site_map_id} to={`/hse/site-maps/${m.site_map_id}`}
                  className="group flex gap-3 rounded border border-line hover:border-accent/40 hover:bg-ink-hover transition-colors overflow-hidden">
                  <div className="w-[84px] h-[72px] shrink-0 bg-[#0b0f14] relative overflow-hidden">
                    {thumb && <img src={thumb} alt="" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" loading="lazy"/>}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="w-2.5 h-2.5 rounded-full bg-accent ring-2 ring-white/90 shadow"/>
                    </div>
                  </div>
                  <div className="py-2 pr-3 min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[11px] text-accent">{m.map_number}</span>
                      {m.is_locked
                        ? <Pill label={`locked v${m.version}`} tone="ok" small/>
                        : <Pill label="open" tone="pending" small/>}
                      {fromBid && <Pill label="from the BID" tone="bid" small/>}
                    </div>
                    <div className="text-[13px] text-text truncate">{m.title}</div>
                    <div className="text-[11px] text-text-muted flex items-center gap-2">
                      <span>{n} {n === 1 ? "thing" : "things"} drawn</span>
                      {m.locked_at && <span className="flex items-center gap-1"><Lock size={10}/>{fmtDate(m.locked_at)}</span>}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
}
